import { NumberInput } from '@/components/ui/number-input';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

interface EditablePriceCellProps {
  /** Current value in cents, or null when unset. */
  valueCents: number | null;
  /** Commit a new value in cents, or null to clear (only when allowEmpty). */
  onCommit: (cents: number | null) => void;
  /** Whether clearing the cell is allowed (custom price yes, base price no). */
  allowEmpty?: boolean;
  isPending?: boolean;
  placeholder?: string;
}

/**
 * A price cell edited inline: shows euros, stores/commits cents. Commits on
 * Enter/blur when the value changed; Escape reverts. Uses `NumberInput`'s
 * `onValueChange` so decimals are preserved (unlike a raw parseFloat).
 */
export function EditablePriceCell({
  valueCents,
  onCommit,
  allowEmpty = false,
  isPending = false,
  placeholder = '—',
}: EditablePriceCellProps) {
  const toEuros = (cents: number | null): number | undefined =>
    cents === null ? undefined : cents / 100;

  const [draft, setDraft] = useState<number | undefined>(toEuros(valueCents));

  // Resync when the server value changes (e.g. after a refetch).
  useEffect(() => {
    setDraft(toEuros(valueCents));
  }, [valueCents]);

  const commit = () => {
    const cents = draft === undefined ? null : Math.round(draft * 100);

    if (cents === null && !allowEmpty) {
      setDraft(toEuros(valueCents)); // revert — base price can't be cleared
      return;
    }
    if (cents !== null && cents <= 0) {
      setDraft(toEuros(valueCents)); // revert — invalid amount
      return;
    }
    if (cents === valueCents) return; // unchanged

    onCommit(cents);
  };

  return (
    <NumberInput
      value={draft}
      onValueChange={setDraft}
      decimalScale={2}
      fixedDecimalScale
      suffix=" €"
      placeholder={placeholder}
      disabled={isPending}
      className={cn('h-8 w-28', isPending && 'opacity-50')}
      onBlur={commit}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setDraft(toEuros(valueCents));
          e.currentTarget.blur();
        }
      }}
    />
  );
}
